// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, AvatarImg } from "./common";
import { useEscapeKey } from "../components/useEscapeKey";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { useNaverMarkers } from "../components/naver/useNaverMarkers";
import { useNaverOverlays } from "../components/naver/useNaverOverlays";
import { MarkerGlyph } from "../components/naver/MarkerGlyph";
import { MARKER_HEX } from "../components/naver/markers";
import { subAreaForCoord, hasPilotBoundary, boundaryFor, SUB_AREA_COLORS } from "../components/naver/pilotBoundary";
import { useNaverRegions, regionColorFor } from "../components/naver/useNaverRegions";

// ─────────────────────────────────────────────────────────────────────────
// 권역 지도 화면
//
// 좌측: 표시 레이어 + 카테고리 다중 필터 + 가용 매니저 리스트
// 가운데: 검색 입력 + SVG 지도 + 마커 (검색/필터에 따라 dim/hide)
// 우측: 선택된 마커의 상세 (어르신/매니저/기관 3종 분기)
//
// 필터는 모두 클라이언트 사이드 — 권역 단위 데이터는 한 번 받아오면 수십~수백
// 마커 수준이라 다시 fetch할 필요가 없음.
// ─────────────────────────────────────────────────────────────────────────

const CAT_LABEL = { hospital: "병원", health: "보건", fire: "소방", welfare: "복지", care: "요양" };
const CAT_COLOR = { hospital: "rose", health: "green", fire: "orange", welfare: "sky", care: "amber" };
// 범례/상세의 픽토그램은 지도 마커와 같은 것을 쓴다(markers.ts GLYPHS). 예전엔
// 앱 아이콘 세트를 따로 골라 써서 복지·요양이 같은 아이콘이었다.
const CAT_GLYPH = { hospital: "hospital", health: "health", fire: "fire", welfare: "welfare", care: "care" };
const STATUS_LABEL = { available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인" };
const STATUS_COLOR = { available: "green", busy: "amber", overloaded: "rose", moving: "orange", offline: "gray" };
const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR = { critical: "rose", warning: "amber", stable: "green" };
const CONTRACT_LABEL = { completed: "협약 완료", reviewing: "검토중", linked: "연동중", none: "미체결" };
const CONTRACT_COLOR = { completed: "green", reviewing: "amber", linked: "green", none: "rose" };
// 권역 관리 패널용 — 권역 카드의 등급/운영상태 배지.
const GRADE_LABEL = { excellent: "우수", good: "양호", needs_improvement: "개선필요", pilot: "시범" };
const GRADE_COLOR = { excellent: "green", good: "sky", needs_improvement: "rose", pilot: "amber" };
const OPSTATUS_LABEL = { operating: "운영중", pilot: "시범", suspended: "중단", closed: "폐쇄" };

const ALL_CAT_KEYS = Object.keys(CAT_LABEL);

// Haversine 직선 거리 (km). 두 점의 위경도가 둘 다 있어야 의미. 한 쪽이라도
// null이면 Infinity를 돌려서 정렬 시 자동으로 뒤로 밀리도록 한다.
function haversineKm(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// km 사람 가독 포맷: 1km 미만은 m, 그 외는 소수 한 자리.
function formatKm(km) {
  if (!Number.isFinite(km)) return "-";
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
}

// 좌표 기준으로 후보 목록을 거리 오름차순 N개로 잘라 반환. 각 항목에
// `_km` 필드를 부여해 패널에서 그대로 렌더링.
function nearestBy(originLat, originLng, candidates, n = 4) {
  return candidates
    .map((c) => ({ ...c, _km: haversineKm(originLat, originLng, c.lat, c.lng) }))
    .filter((c) => Number.isFinite(c._km))
    .sort((a, b) => a._km - b._km)
    .slice(0, n);
}

// 검색 매치 판정. needle이 비면 항상 true, 그 외엔 entity의 도메인별 토큰 중
// 하나라도 contains 되면 true.
function matchesSearch(entity, kind, needle) {
  if (!needle) return true;
  const tokens =
      kind === "elderly" ? [entity.name, entity.health_status, entity.address]
    : kind === "manager" ? [entity.name, entity.qualification, entity.organization_name]
    : kind === "org"     ? [entity.name, entity.address, CAT_LABEL[entity.category]]
    : [];
  return tokens.filter(Boolean).some(t => t.toLowerCase().includes(needle));
}

function MapView() {
  const [data, setData] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [query, setQuery] = React.useState("");
  // 레이어 토글 — 사용자가 끄면 해당 종류 마커가 화면에서 사라짐.
  const [layers, setLayers] = React.useState({ managers: true, elderlies: true, orgs: true });
  // 카테고리 다중 토글 (기관에만 적용). 기본은 5개 모두 활성.
  const [activeCats, setActiveCats] = React.useState(() => new Set(ALL_CAT_KEYS));
  // 오버레이 토글: 권역 경계 / 케이스 동선 / 거점 커버리지 반경 / 권역 관리 레이어.
  // coverage 는 김해(파일럿) 권역 진입에서만 실질적 의미 — 다른 권역엔 앵커 데이터 없음.
  const [overlays, setOverlays] = React.useState({ boundary: true, links: true, coverage: true, regions: true });
  const toggleOverlay = React.useCallback((key) => {
    setOverlays(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ── 권역 관리 레이어 ────────────────────────────────────────
  // 권역 요약(집계 + bbox)은 점 데이터와 별도 엔드포인트. 지도 위 경계/라벨과
  // 우측 "권역 관리" 패널이 같은 데이터를 공유한다.
  const [regions, setRegions] = React.useState([]);
  const [regionPanelOpen, setRegionPanelOpen] = React.useState(false);
  // 선택 권역. null이면 전체(전국) 보기. URL(?region_id=)과 양방향 동기화.
  const [regionId, setRegionIdState] = React.useState(() => {
    const v = new URLSearchParams(window.location.search).get("region_id");
    return v && v !== "all" ? parseInt(v, 10) : null;
  });

  React.useEffect(() => {
    fetch("/api/v1/map/regions", { credentials: "same-origin" })
      .then(r => (r.ok ? r.json() : { regions: [] }))
      .then(d => setRegions(d.regions || []))
      .catch(() => {});
  }, []);

  // 권역 선택 → 점 데이터 재조회(아래 effect) + 지도 이동(useNaverRegions) + URL 갱신.
  // 하위 구역은 권역에 종속이므로 권역이 바뀌면 함께 푼다.
  const selectRegion = React.useCallback((id) => {
    setRegionIdState(id);
    setSubAreaState("");
    const params = new URLSearchParams(window.location.search);
    if (id == null) params.delete("region_id");
    else            params.set("region_id", String(id));
    params.delete("sub_area");
    const url = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", url);
  }, []);

  // 하위 구역(파일럿 권역용) 필터 상태. URL(?sub_area=)과 양방향으로 동기화된다 —
  // 초기 로드는 URL 값을 시드로 쓰고, 이후 변경은 URL 을 replaceState 로 갱신해
  // 새로고침·공유 링크에도 상태가 살아남게 한다.
  const [subArea, setSubAreaState] = React.useState(() => {
    return new URLSearchParams(window.location.search).get("sub_area") || "";
  });

  // ?elderly_id=N 진입: 그 어르신을 자동 선택 + (김해 파일럿이면) 좌표가 속한
  // 권역을 자동 판별해 해당 권역으로 fitBounds. "누가 어떤 서비스 중인지" 뷰라
  // 링크만 던지면 바로 그 사람 컨텍스트로 열려야 한다.
  const targetElderlyId = React.useMemo(() => {
    const p = new URLSearchParams(window.location.search).get("elderly_id");
    return p ? parseInt(p, 10) : null;
  }, []);
  const didAutoNarrowRef = React.useRef(false);

  // 권역관리 카드 / mgr-home 다음 방문 카드 등 여러 진입점이 있다.
  //   /map?region_id=N               → 그 권역으로 점 데이터 한정
  //   /map?region_id=N&sub_area=북부 → 파일럿 권역의 하위 구역까지 좁힘
  //   /map?visit_id=N                → 그 방문의 어르신으로 자동 포커스
  //   /map?elderly_id=N              → 그 어르신 자동 선택 + 소속 권역 자동 확대
  // subArea/regionId 변경 시에도 재조회 — 그래야 구역·권역 이동이 즉시 반영된다.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const visitId    = params.get("visit_id");
    const elderlyId  = params.get("elderly_id");
    const qs = new URLSearchParams();
    if (regionId != null) qs.set("region_id", String(regionId));
    if (visitId)   qs.set("visit_id",   visitId);
    if (elderlyId) qs.set("elderly_id", elderlyId);   // 백엔드가 무시해도 무해
    if (subArea)   qs.set("sub_area",   subArea);
    const url = qs.toString() ? `/api/v1/map/points?${qs}` : "/api/v1/map/points";
    fetch(url, { credentials: "same-origin" }).then(r => r.json()).then(d => {
      setData(d);

      // 우선순위: URL elderly_id > 백엔드 focus > 첫 어르신
      if (targetElderlyId != null) {
        const found = d.elderlies?.find(e => e.id === targetElderlyId);
        if (found) {
          setSelected({ kind: "elderly", id: targetElderlyId });
          // 경계 데이터를 가진 파일럿 권역(김해·남양주 등)에서 자동 좁힘.
          // 최초 1회, sub_area 미지정 시에만.
          if (!didAutoNarrowRef.current && !subArea) {
            didAutoNarrowRef.current = true;
            const key = subAreaForCoord(d.region_code, found.lat, found.lng);
            if (key) setSubArea(key);  // 재조회 → 해당 권역으로 fitBounds
          }
          return;
        }
      }
      if (d.focus) {
        setSelected(d.focus);
      } else if (d.elderlies?.length) {
        setSelected({ kind: "elderly", id: d.elderlies[0].id });
      }
    });
  }, [subArea, regionId]);

  // 사이드바에서 하위 구역 선택 시 상태 + URL 을 동시에 갱신. history push 대신
  // replace 로 뒤로가기 스택을 오염시키지 않는다(같은 지도 화면 안의 필터 조작).
  const setSubArea = React.useCallback((next) => {
    setSubAreaState(next);
    const params = new URLSearchParams(window.location.search);
    if (next) params.set("sub_area", next);
    else      params.delete("sub_area");
    const url = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", url);
  }, []);

  // 권역 관리 패널의 하위 구역 칩 — 권역 이동과 구역 필터를 한 번에.
  // selectRegion이 구역을 푸는(다른 권역으로 옮기면 구역은 무의미) 순서 뒤에
  // 구역을 다시 세팅해야 URL·상태가 모두 남는다.
  const selectRegionSubArea = React.useCallback((id, area) => {
    if (id !== regionId) selectRegion(id);
    setSubArea(area);
  }, [regionId, selectRegion]);

  // 지도 팬/줌(idle) 시 현재 화면 경계로 점 데이터만 다시 받아온다(뷰포트 조회).
  // 백엔드가 bbox 안 + 6000 상한으로 응답 → 기관이 만 단위여도 화면 단위로만 받아
  // 누락/과대 페이로드를 막는다. center/zoom/bbox/focus는 초기값을 유지하고 점 배열만
  // 교체한다(지도가 매 이동마다 재중심되는 것 방지).
  const handleViewport = React.useCallback((b) => {
    const qs = new URLSearchParams();
    if (regionId != null) qs.set("region_id", String(regionId));
    // 하위 구역 필터도 같이 실어야 한다 — 구역 선택 직후 fitBounds가 idle을 유발해
    // 곧바로 이 조회가 돌기 때문에, 빠뜨리면 방금 좁힌 어르신 목록이 화면 전체
    // 목록으로 덮어써져 필터가 안 먹는 것처럼 보인다.
    if (subArea) qs.set("sub_area", subArea);
    qs.set("north", String(b.north)); qs.set("south", String(b.south));
    qs.set("east",  String(b.east));  qs.set("west",  String(b.west));
    fetch(`/api/v1/map/points?${qs}`, { credentials: "same-origin" })
      .then(r => r.json())
      .then(d => setData(prev => prev ? {
        ...prev,
        organizations: d.organizations,
        care_managers: d.care_managers,
        elderlies:     d.elderlies,
        case_links:    d.case_links,
        counts:        d.counts,
        truncated:     d.truncated,
      } : d))
      .catch(() => {});
  }, [regionId, subArea]);

  // ESC 스택: 상세 패널 → 권역 관리 패널 순서로 하나씩 닫힘. 두 오버레이가
  // 겹쳐 열려있어도 사용자가 최상단부터 자연스럽게 되돌아갈 수 있게 한다.
  useEscapeKey(() => {
    if (selected)               setSelected(null);
    else if (regionPanelOpen)   setRegionPanelOpen(false);
  }, !!selected || regionPanelOpen);

  // ── 파생 데이터 (가시 마커 계산) ────────────────────────────
  const needle = query.trim().toLowerCase();

  const orgVisibility = React.useMemo(() => {
    if (!data) return new Map();
    const m = new Map();
    for (const o of data.organizations) {
      const inLayer = layers.orgs;
      const inCat   = activeCats.has(o.category);
      const inSearch = matchesSearch(o, "org", needle);
      m.set(o.id, { rendered: inLayer && inCat, dim: !inSearch });
    }
    return m;
  }, [data, layers.orgs, activeCats, needle]);

  const elderlyVisibility = React.useMemo(() => {
    if (!data) return new Map();
    const m = new Map();
    for (const e of data.elderlies) {
      const inLayer  = layers.elderlies;
      const inSearch = matchesSearch(e, "elderly", needle);
      m.set(e.id, { rendered: inLayer, dim: !inSearch });
    }
    return m;
  }, [data, layers.elderlies, needle]);

  const managerVisibility = React.useMemo(() => {
    if (!data) return new Map();
    const m = new Map();
    for (const cm of data.care_managers) {
      const inLayer  = layers.managers;
      const inSearch = matchesSearch(cm, "manager", needle);
      m.set(cm.id, { rendered: inLayer, dim: !inSearch });
    }
    return m;
  }, [data, layers.managers, needle]);

  // 선택된 마커가 필터로 사라지면 자동 해제 — 우측 패널이 빈 entity로 깨지는
  // 걸 방지하기 위함. selected를 primitive 키(kind:id)로 좁혀 매 렌더 참조
  // 흔들림을 제거 — visibility Map은 이미 memoized라 실제 필터 변화 때만 재실행.
  const selectedKey = selected ? `${selected.kind}:${selected.id}` : null;
  React.useEffect(() => {
    if (!selected || !data) return;
    const vis = selected.kind === "elderly" ? elderlyVisibility
             : selected.kind === "manager"  ? managerVisibility
             : selected.kind === "org"      ? orgVisibility
             : null;
    if (!vis) return;
    const v = vis.get(selected.id);
    if (!v || !v.rendered) setSelected(null);
  }, [selectedKey, elderlyVisibility, managerVisibility, orgVisibility]);

  // 매치/전체 카운트 — 사이드바 라벨에 사용.
  const counts = React.useMemo(() => {
    if (!data) return null;
    const countVis = (vis, list) => list.reduce((n, x) => {
      const v = vis.get(x.id);
      return n + (v?.rendered && !v.dim ? 1 : 0);
    }, 0);
    return {
      managers:    { match: countVis(managerVisibility, data.care_managers), total: data.care_managers.length },
      elderlies:   { match: countVis(elderlyVisibility, data.elderlies),     total: data.elderlies.length },
      orgs:        { match: countVis(orgVisibility, data.organizations),     total: data.organizations.length },
    };
  }, [data, managerVisibility, elderlyVisibility, orgVisibility]);

  // 카테고리별 카운트 (orgs 기준, 검색/필터 반영). 검색 시 "병원 15" 처럼 전체
  // 개수가 그대로 남으면 실제 화면에 보이는 마커 수와 어긋나 신뢰가 깨진다 —
  // visibility Map(rendered && !dim)을 그대로 반영해 범례 카운트가 지도와 정확히
  // 일치하도록 한다.
  const orgsByCat = React.useMemo(() => {
    if (!data) return {};
    const m = {};
    for (const o of data.organizations) {
      const v = orgVisibility.get(o.id);
      if (v && v.rendered && !v.dim) {
        m[o.category] = (m[o.category] || 0) + 1;
      }
    }
    return m;
  }, [data, orgVisibility]);

  // 지도 상단 통계 칩 — 클릭 한 번으로 "그 종류만" 보기. 같은 칩을 다시 누르면
  // 전체로 복귀한다(gcare 지역지도의 상단 필터바와 같은 동작).
  // 사이드바에서 레이어를 개별 조작하면 프리셋은 custom으로 풀린다.
  const [preset, setPreset] = React.useState("all");

  const toggleLayer = React.useCallback((key) => {
    setLayers(prev => ({ ...prev, [key]: !prev[key] }));
    setPreset("custom");
  }, []);

  const applyPreset = React.useCallback((key) => {
    const next = preset === key && key !== "all" ? "all" : key;
    setPreset(next);
    setLayers(next === "all"
      ? { managers: true, elderlies: true, orgs: true }
      : { managers: next === "managers", elderlies: next === "elderlies", orgs: next === "orgs" });
  }, [preset]);

  const toggleCategory = React.useCallback((key) => {
    setActiveCats(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    // "전체" 프리셋 상태에서 개별 카테고리를 끄면 더 이상 전체가 아니다 —
    // 프리셋 표시와 실제 필터가 어긋나 사용자가 헷갈리는 것 방지.
    setPreset("custom");
  }, []);

  // 범례의 "전체 / 해제" 일괄 버튼 (gcare 지역지도 레전드와 동일한 조작).
  const setAllLayers = React.useCallback((on) => {
    setLayers({ managers: on, elderlies: on, orgs: on });
    setPreset(on ? "all" : "custom");
  }, []);
  const setAllCategories = React.useCallback((on) => {
    setActiveCats(on ? new Set(ALL_CAT_KEYS) : new Set());
    if (!on) setPreset("custom");
  }, []);

  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  // 권역 레이어가 실제로 그려지는 중인지 — 이때는 권역 사각형이 곧 경계라서
  // 레거시 bbox 경계 rect(같은 선 두 겹)를 그리지 않고 토글도 감춘다.
  // 파일럿 권역(김해·남양주)은 예외 — 여기 "권역 경계"는 bbox rect가 아니라 시경계 +
  // 하위 구역 폴리곤이라 권역 레이어와 겹치지 않는다.
  const isPilotRegion     = hasPilotBoundary(data.region_code);
  const pilotSubAreaCount = boundaryFor(data.region_code)?.subAreas.length ?? 0;
  const regionLayerActive = regions.length > 0 && overlays.regions && !isPilotRegion;
  const center = data.center;
  const availableMgrs = data.care_managers.filter(m => m.status === "available").slice(0, 4);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        {/* 사이드바는 로그인 user.role을 자동 인식 — propRole은 비로그인 폴백용. */}
        <Sidebar active="map"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="권역 지도" sub={`기관 ${data.organizations.length.toLocaleString()} · 매니저 ${data.care_managers.length.toLocaleString()} · 어르신 ${data.elderlies.length.toLocaleString()}` + (
            data.truncated && (data.truncated.organizations || data.truncated.care_managers || data.truncated.elderlies)
              ? ` · 표시 상한 적용(전체 기관 ${data.counts.organizations.toLocaleString()} · 매니저 ${data.counts.care_managers.toLocaleString()} · 어르신 ${data.counts.elderlies.toLocaleString()}, 위험순 상위 표시)`
              : ''
          )}/>
          {/* 지도는 화면 전체 폭. 필터/범례·선택 상세는 모두 지도 위에 떠 있는
              레이어다(gcare 지역지도 방식) — 좌우 컬럼을 없애 지도 면적을 최대화. */}
          <div style={{flex:1, position:'relative', overflow:'hidden'}}>

            <MapCanvas
              data={data}
              center={center}
              query={query}
              onChangeQuery={setQuery}
              selected={selected}
              onSelect={setSelected}
              orgVisibility={orgVisibility}
              elderlyVisibility={elderlyVisibility}
              managerVisibility={managerVisibility}
              hasResults={!!counts && (counts.managers.match + counts.elderlies.match + counts.orgs.match) > 0}
              showBoundary={overlays.boundary && !regionLayerActive}
              showLinks={overlays.links}
              showCoverage={overlays.coverage}
              subAreas={data.region_sub_areas || []}
              selectedSubArea={subArea}
              onChangeSubArea={setSubArea}
              onViewportChange={handleViewport}
              regions={regions}
              showRegions={overlays.regions}
              selectedRegionId={regionId}
              onSelectRegion={selectRegion}
              regionPanelOpen={regionPanelOpen}
              onOpenRegionPanel={() => setRegionPanelOpen(true)}
              onCloseRegionPanel={() => setRegionPanelOpen(false)}
              onSelectSubArea={selectRegionSubArea}
              preset={preset}
              onApplyPreset={applyPreset}
              counts={counts}
              regionOwnsCamera={regionLayerActive}
              detailOpen={!!selected}
              legend={
                <MapLegend
                  counts={counts}
                  layers={layers}
                  onToggleLayer={toggleLayer}
                  onSetAllLayers={setAllLayers}
                  activeCats={activeCats}
                  onToggleCategory={toggleCategory}
                  onSetAllCategories={setAllCategories}
                  orgsByCat={orgsByCat}
                  availableMgrs={availableMgrs}
                  onPickManager={(id) => setSelected({ kind: "manager", id })}
                  overlays={overlays}
                  onToggleOverlay={toggleOverlay}
                  hasBoundary={!!data.bbox}
                  linksCount={(data.case_links || []).length}
                  subAreas={data.region_sub_areas || []}
                  selectedSubArea={subArea}
                  onChangeSubArea={setSubArea}
                  isPilotRegion={isPilotRegion}
                  pilotSubAreaCount={pilotSubAreaCount}
                  hasRegions={regions.length > 0}
                  regionLayerActive={regionLayerActive}
                />
              }
              detail={
                <SelectionPanel
                  data={data}
                  selected={selected}
                  onSelect={setSelected}
                  onClose={() => setSelected(null)}
                  shiftedLeft={regionPanelOpen}
                  /* 권역 관리 FAB(top:20)을 가리지 않도록 한 칸 아래에서 시작.
                     패널이 열려 FAB이 사라지면 위쪽 줄에 맞춰 올라간다. */
                  topOffset={regionPanelOpen ? 20 : 76}
                />
              }
            />

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

// ─── Filter Sidebar ──────────────────────────────────────────────────

// 지도 위에 떠 있는 범례 겸 필터. 예전엔 320px 좌측 컬럼(aside)이었지만, 지도를
// 화면 전체로 넓히면서 gcare 지역지도처럼 지도 위 레이어로 옮겼다. 접으면 작은
// 버튼만 남아 지도를 하나도 가리지 않는다.
function MapLegend({ counts, layers, onToggleLayer, onSetAllLayers, activeCats, onToggleCategory, onSetAllCategories, orgsByCat, availableMgrs, onPickManager, overlays, onToggleOverlay, hasBoundary, linksCount, subAreas, selectedSubArea, onChangeSubArea, isPilotRegion, pilotSubAreaCount, hasRegions, regionLayerActive }) {
  const [open, setOpen] = React.useState(true);
  const layerRows = [
    { key: "managers",  label: "케어매니저",     color: "orange", sub: `가용 ${availableMgrs.length}` },
    { key: "elderlies", label: "어르신 케이스", color: "green",  sub: "위험 강조" },
    { key: "orgs",      label: "유관기관",       color: "sky",    sub: "" },
  ];

  if (!open) {
    return (
      <button type="button"
        onClick={() => setOpen(true)}
        title="범례·필터 열기"
        aria-label="범례·필터 열기"
        style={{
          position:'absolute', left:20, bottom:46, zIndex:11, pointerEvents:'auto',
          display:'flex', alignItems:'center', gap:8, padding:'10px 14px',
          borderRadius:12, cursor:'pointer', fontSize:12, fontWeight:700,
          background:'rgba(255,255,255,0.96)', border:'1px solid rgba(20,25,40,0.08)',
          boxShadow:'var(--shadow-md)', color:'var(--ink-1)',
        }}>
        <span style={{display:'grid', placeItems:'center', fontSize:16, width:16, height:16, color:'var(--orange-600)'}}>{Ic.dashboard}</span>
        범례·필터
      </button>
    );
  }

  return (
    <div
      aria-label="지도 범례 및 필터"
      style={{
        position:'absolute', left:20, bottom:46, zIndex:11, pointerEvents:'auto',
        width:266, maxHeight:'62%', display:'flex', flexDirection:'column',
        background:'rgba(255,255,255,0.96)', backdropFilter:'blur(12px)',
        border:'1px solid rgba(20,25,40,0.08)', borderRadius:14,
        boxShadow:'0 10px 30px rgba(20,25,40,0.16)', overflow:'hidden',
      }}>
      <div style={{display:'flex', alignItems:'center', gap:8, padding:'10px 12px',
                   borderBottom:'1px solid var(--border-soft)', flex:'0 0 auto'}}>
        <span style={{display:'grid', placeItems:'center', fontSize:15, width:15, height:15, color:'var(--orange-600)'}}>{Ic.dashboard}</span>
        <div style={{flex:1, fontSize:12.5, fontWeight:800}}>범례 · 필터</div>
        <button type="button" onClick={() => setOpen(false)} title="접기" aria-label="범례 접기"
          style={{width:24, height:24, display:'grid', placeItems:'center', borderRadius:7,
                  background:'transparent', border:'1px solid var(--border-soft)',
                  color:'var(--ink-3)', cursor:'pointer'}}>
          {Ic.close}
        </button>
      </div>

      <div style={{overflowY:'auto', padding:'10px 12px 12px'}}>
      <LegendSectionTitle title="표시 레이어" onAll={() => onSetAllLayers(true)} onNone={() => onSetAllLayers(false)}/>
      {layerRows.map((row) => {
        const on = layers[row.key];
        const c  = counts?.[row.key];
        return (
          <button key={row.key} type="button"
            onClick={() => onToggleLayer(row.key)}
            aria-pressed={on}
            style={{
              display:'flex', alignItems:'center', gap:10, padding:'10px 8px',
              borderRadius:8, background: on ? 'var(--bg-sub)' : 'var(--surface)',
              border: on ? '1px solid transparent' : '1px dashed var(--border-soft)',
              width:'100%', cursor:'pointer', marginBottom:6, textAlign:'left',
              opacity: on ? 1 : 0.5, transition:'opacity 0.15s, background 0.15s',
            }}>
            <span style={{
              width:14, height:14, borderRadius:4,
              background: on ? `var(--${row.color}-500)` : 'transparent',
              border: on ? 'none' : '1.5px solid var(--ink-4)',
              display:'grid', placeItems:'center', color:'#fff',
            }}>
              {on && <span style={{width:10, height:10, display:'inline-block'}}>{Ic.check}</span>}
            </span>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:12.5, fontWeight:600}}>{row.label}</div>
              {row.sub && <div className="muted" style={{fontSize:10}}>{row.sub}</div>}
            </div>
            {c && (
              <span className="mono" style={{fontSize:11, fontWeight:700, color: on ? 'var(--ink-1)' : 'var(--ink-3)'}}>
                {c.match === c.total ? c.total : `${c.match}/${c.total}`}
              </span>
            )}
          </button>
        );
      })}

      {/* 하위 구역(파일럿 권역 전용) — 김해처럼 sub_areas 가 잡힌 권역에서만 노출.
          "전체"는 필터 해제. 선택된 값은 URL(?sub_area=) 로 replaceState 되어 새로
          고침/공유에도 살아남는다. 시도 권역(sub_areas 비어있음)에서는 섹션 자체가
          렌더되지 않아 기존 시도 지도 UX 를 깨지 않는다. */}
      {Array.isArray(subAreas) && subAreas.length > 0 && (
        <div style={{marginTop:14}}>
          <LegendSectionTitle title="하위 구역"/>
          <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
            <button type="button"
              onClick={() => onChangeSubArea("")}
              aria-pressed={!selectedSubArea}
              style={{padding:'6px 10px', borderRadius:14, fontSize:11.5, fontWeight:600, cursor:'pointer',
                      border: !selectedSubArea ? '1px solid var(--orange-500)' : '1px solid var(--border-soft)',
                      background: !selectedSubArea ? 'var(--orange-50)' : 'var(--surface)',
                      color: !selectedSubArea ? 'var(--orange-700)' : 'var(--ink-2)'}}>
              전체
            </button>
            {subAreas.map(area => {
              const on = selectedSubArea === area;
              return (
                <button key={area} type="button"
                  onClick={() => onChangeSubArea(area)}
                  aria-pressed={on}
                  style={{padding:'6px 10px', borderRadius:14, fontSize:11.5, fontWeight:600, cursor:'pointer',
                          border: on ? '1px solid var(--orange-500)' : '1px solid var(--border-soft)',
                          background: on ? 'var(--orange-50)' : 'var(--surface)',
                          color: on ? 'var(--orange-700)' : 'var(--ink-2)'}}>
                  {area}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 권역 경계 / 동선 라인 / 거점 커버리지 오버레이 토글. 마커 레이어와 별도. */}
      {(hasBoundary || linksCount > 0 || isPilotRegion || hasRegions) && (
        <div style={{marginTop:14}}>
          <LegendSectionTitle title="오버레이"/>
          {hasRegions && (
            <OverlayToggleRow
              label="권역 레이어"
              hint="권역 경계 + 라벨 (우측 권역 관리)"
              accent="orange"
              on={overlays.regions}
              onClick={() => onToggleOverlay("regions")}
            />
          )}
          {(hasBoundary || isPilotRegion) && !regionLayerActive && (
            <OverlayToggleRow
              label="권역 경계"
              hint={isPilotRegion ? `시경계 + 하위 ${pilotSubAreaCount}구역 폴리곤` : "bbox 기반 사각형"}
              accent="orange"
              on={overlays.boundary}
              onClick={() => onToggleOverlay("boundary")}
            />
          )}
          {isPilotRegion && (
            <OverlayToggleRow
              label="거점 커버리지"
              hint="복지관 앵커 반경(5~6km)"
              accent="orange"
              on={overlays.coverage}
              onClick={() => onToggleOverlay("coverage")}
            />
          )}
          {linksCount > 0 && (
            <OverlayToggleRow
              label="매칭 동선"
              hint={`${linksCount}건 배정 케이스`}
              accent="orange"
              on={overlays.links}
              onClick={() => onToggleOverlay("links")}
            />
          )}
        </div>
      )}

      <div style={{marginTop:14}}>
        <LegendSectionTitle title="유관기관 분류"
          onAll={() => onSetAllCategories(true)} onNone={() => onSetAllCategories(false)}/>
        {Object.entries(CAT_LABEL).map(([k, l], i) => {
          const on = activeCats.has(k);
          const c  = CAT_COLOR[k];
          return (
            <button key={k} type="button"
              onClick={() => onToggleCategory(k)}
              aria-pressed={on}
              disabled={!layers.orgs}
              title={!layers.orgs ? "유관기관 레이어가 꺼져있습니다" : on ? "이 카테고리 숨김" : "이 카테고리 표시"}
              style={{
                display:'flex', alignItems:'center', gap:10, padding:'8px 6px',
                borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
                width:'100%', cursor: layers.orgs ? 'pointer' : 'not-allowed',
                background: 'transparent', border:'none',
                opacity: on && layers.orgs ? 1 : 0.4,
                textAlign:'left',
              }}>
              {/* 지도 마커와 동일한 색·픽토그램 — 범례가 마커의 축소판이 되게. */}
              <span style={{
                width:26, height:26, borderRadius:8,
                background: MARKER_HEX.category[k],
                border: on ? '2px solid #fff' : '1px dashed var(--border)',
                boxShadow: on ? '0 2px 5px rgba(0,0,0,0.14)' : 'none',
                display:'grid', placeItems:'center',
              }}>
                <MarkerGlyph glyph={CAT_GLYPH[k]} size={14} bg={MARKER_HEX.category[k]}/>
              </span>
              <span style={{flex:1, fontSize:12.5}}>{l}</span>
              <span className="mono muted" style={{fontSize:11}}>{orgsByCat[k] || 0}</span>
            </button>
          );
        })}
      </div>

      <div style={{marginTop:14}}>
        <LegendSectionTitle title="가용 매니저 (상위)"/>
        {availableMgrs.length === 0 && (
          <div className="muted" style={{fontSize:11, padding:'8px 0'}}>가용 매니저가 없습니다.</div>
        )}
        {availableMgrs.map((m, i) => (
          <button key={m.id} type="button"
            onClick={() => onPickManager(m.id)}
            style={{
              display:'flex', alignItems:'center', gap:10, padding:'8px 0',
              borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
              width:'100%', background:'transparent', border:'none', cursor:'pointer',
              textAlign:'left',
            }}>
            <div className="avatar sm">{m.name[0]}</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:12.5, fontWeight:600}}>{m.name}</div>
              <div className="muted" style={{fontSize:10}}>{m.qualification} · {STATUS_LABEL[m.status]}</div>
            </div>
            <span className="dot" style={{background:'var(--green-500)'}}/>
          </button>
        ))}
      </div>
      </div>
    </div>
  );
}

// 범례 섹션 헤더 + 일괄 토글(전체/해제). gcare 레전드의 ml-btn 대응.
function LegendSectionTitle({ title, onAll, onNone }) {
  const btn = {
    fontSize:9.5, fontWeight:700, padding:'2px 6px', borderRadius:5, cursor:'pointer',
    background:'var(--bg-sub)', border:'1px solid var(--border-soft)', color:'var(--ink-2)',
  };
  return (
    <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:7}}>
      <span style={{fontSize:10.5, fontWeight:800, letterSpacing:'0.02em', color:'var(--ink-1)'}}>{title}</span>
      {onAll && onNone && (
        <span style={{marginLeft:'auto', display:'flex', gap:4}}>
          <button type="button" style={btn} onClick={onAll}>전체</button>
          <button type="button" style={btn} onClick={onNone}>해제</button>
        </span>
      )}
    </div>
  );
}

// 오버레이 토글 행 — 권역 경계 / 동선 라인 등 마커 외 시각 요소용.
// 레이어 토글과 시각이 살짝 달라야 사용자가 마커 레이어와 헷갈리지 않음.
function OverlayToggleRow({ label, hint, accent = "orange", on, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      style={{
        display:'flex', alignItems:'center', gap:10, padding:'8px 8px',
        borderRadius:8, background: on ? `var(--${accent}-50)` : 'var(--surface)',
        border: on ? `1px solid var(--${accent}-200)` : '1px dashed var(--border-soft)',
        width:'100%', cursor:'pointer', marginBottom:6, textAlign:'left',
        opacity: on ? 1 : 0.55, transition:'opacity 0.15s, background 0.15s',
      }}>
      <span style={{
        width:24, height:14, borderRadius:99,
        background: on ? `var(--${accent}-500)` : 'var(--ink-4)',
        position:'relative', flex:'0 0 auto',
      }}>
        <span style={{
          position:'absolute', top:1, left: on ? 11 : 1,
          width:12, height:12, borderRadius:'50%', background:'#fff',
          boxShadow:'0 1px 2px rgba(0,0,0,0.2)',
          transition:'left 0.15s',
        }}/>
      </span>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:12, fontWeight:700}}>{label}</div>
        {hint && <div className="muted" style={{fontSize:10}}>{hint}</div>}
      </div>
    </button>
  );
}

// ─── Map Canvas ──────────────────────────────────────────────────────

// 단계 2: SVG 시안 배경 + 백분율 좌표 마커를 네이버 지도 SDK로 교체.
// 마커/오버레이 렌더는 단계 3-4에서 NaverMapCanvas 내부 effect로 다시 들어온다.
// 현재 시점에는 빈 지도 + 검색박스(absolute children)만 존재.
//
// 인자 중 selected/orgVisibility/elderlyVisibility/managerVisibility/showBoundary/showLinks는
// 단계 3-4를 위해 시그니처만 유지 — 호출처(MapView) 수정 없이 점진 마이그레이션 가능.
// 구역 색은 pilotBoundary.ts가 단일 출처 — 지도 폴리곤/마커 도트/픽커 pill이 같은 색.
const SUB_AREA_HEX = SUB_AREA_COLORS;

function MapCanvas({ data, center, query, onChangeQuery, selected, onSelect, orgVisibility, elderlyVisibility, managerVisibility, hasResults, showBoundary, showLinks, showCoverage, subAreas, selectedSubArea, onChangeSubArea, onViewportChange, regions, showRegions, selectedRegionId, onSelectRegion, regionPanelOpen, onOpenRegionPanel, onCloseRegionPanel, preset, onApplyPreset, counts, regionOwnsCamera, legend, detail, detailOpen, onSelectSubArea }) {
  // NaverMapCanvas는 지도 인스턴스만 책임. 마커는 인스턴스가 잡힌 뒤 hook이 관리한다.
  const [mapInstance, setMapInstance] = React.useState(null);

  // 진입 시점의 bbox만 고정해 둔다. 권역 레이어가 카메라를 잡은 뒤에도 최초
  // zoom-to-fit(/map?region_id=N 진입)은 그대로 살리되, 이후 권역 전환에서
  // NaverMapCanvas가 대칭 fit으로 덮어쓰지 않게 하기 위함.
  const initialBboxRef = React.useRef(data.bbox ?? null);

  // 팬/줌이 멈추면(idle) 현재 화면 경계를 부모로 올려 뷰포트 조회를 트리거.
  // 350ms 디바운스로 연속 이동 중 과도한 요청을 막는다.
  React.useEffect(() => {
    if (!mapInstance || !window.naver?.maps || !onViewportChange) return;
    let t = null;
    const fire = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        try {
          const bnds = mapInstance.getBounds();
          const ne = bnds.getNE(); const sw = bnds.getSW();
          onViewportChange({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() });
        } catch { /* 줌이 깊거나 bounds 미가용 시 스킵 */ }
      }, 350);
    };
    const listener = window.naver.maps.Event.addListener(mapInstance, "idle", fire);
    return () => { window.clearTimeout(t); window.naver.maps.Event.removeListener(listener); };
  }, [mapInstance, onViewportChange]);

  // 마커 총량이 임계를 넘으면 자동으로 그리드 클러스터링 ON. 권역 한정 진입(/map?region_id=N)
  // 은 보통 수십 건 → OFF. 전국 진입(/m/map)은 수백 건 → ON.
  // 임계값은 시각/성능 trade-off — 100건 정도까지는 개별 마커가 가독성 우수.
  const CLUSTER_THRESHOLD = 100;
  const totalMarkers =
    (data.organizations?.length ?? 0) +
    (data.elderlies?.length ?? 0) +
    (data.care_managers?.length ?? 0);

  // 우측에 떠 있는 패널(권역 관리 / 선택 상세)이 차지하는 폭. 상단 검색·칩 줄의
  // 오른쪽 여백이자, 지도 fitBounds가 확보해야 할 여백이기도 하다 — 그래야 방금
  // 고른 권역·구역이 패널 뒤로 숨지 않는다.
  const topRightInset = regionPanelOpen && detailOpen ? 756
                      : regionPanelOpen || detailOpen ? 410
                      : 120;

  useNaverMarkers({
    map: mapInstance,
    data,
    orgVisibility,
    elderlyVisibility,
    managerVisibility,
    selected,
    onSelect,
    clustering: totalMarkers > CLUSTER_THRESHOLD,
  });

  useNaverOverlays({
    map: mapInstance,
    bbox: data.bbox,
    links: data.case_links,
    elderlies: data.elderlies,
    managers: data.care_managers,
    showBoundary,
    showLinks,
    selected,
    regionCode:      data.region_code,
    selectedSubArea: selectedSubArea,
    showCoverage:    showCoverage,
    fitRightPadding: topRightInset,
  });

  // 권역 관리 레이어 — 권역 경계 사각형 + 클릭 가능한 권역 라벨.
  useNaverRegions({
    map: mapInstance,
    regions,
    show: showRegions,
    selectedId: selectedRegionId ?? null,
    onSelect: onSelectRegion,
    // 패널(우측 330px + 여백)이 열려 있으면 그만큼을 fit 여백으로 잡는다.
    fitRightPadding: topRightInset,
    // 구역까지 고른 이동이면 카메라는 구역 폴리곤 fit이 가져간다.
    subAreaActive: !!selectedSubArea,
  });


  return (
    <NaverMapCanvas
      center={center}
      zoom={data.zoom}
      /* 권역 레이어가 켜져 있으면 진입 이후의 카메라는 useNaverRegions가 책임진다.
         매번 data.bbox를 넘기면 권역 전환마다 NaverMapCanvas가 뒤늦게 대칭 fit으로
         덮어써(선택한 권역이 우측 패널 뒤로 숨음). */
      bbox={regionOwnsCamera ? initialBboxRef.current : data.bbox}
      onMapReady={setMapInstance}
    >
      {/* Search box (top) — 지도 위 absolute 오버레이. 우측은 권역 FAB/줌 컨트롤 자리. */}
      <div style={{position:'absolute', top:20, left:20, right: topRightInset,
                   display:'flex', flexDirection:'column', gap:8, pointerEvents:'none', zIndex:10,
                   transition:'right 0.18s'}}>
        <div style={{display:'flex', gap:10, pointerEvents:'auto', alignItems:'center', flexWrap:'wrap'}}>
          <div className="searchbar" style={{flex:'1 1 260px', maxWidth:380, position:'relative'}}>
            <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)'}}>{Ic.search}</span>
            <input
              className="input"
              type="search"
              value={query}
              onChange={(e) => onChangeQuery(e.target.value)}
              placeholder="장소·매니저·어르신 검색"
              aria-label="지도 검색"
              style={{paddingLeft:36, background:'#fff', boxShadow:'var(--shadow-md)', borderColor:'transparent'}}
            />
            {query && (
              <button type="button"
                onClick={() => onChangeQuery("")}
                aria-label="검색어 지우기"
                style={{
                  position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                  background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer',
                  padding:2,
                }}>
                {Ic.close}
              </button>
            )}
          </div>

          {/* 상단 통계 칩 — 클릭하면 그 레이어만 보기(다시 누르면 전체). */}
          {counts && (
            <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', minWidth:0}}>
              <StatChip label="전체"     value={counts.orgs.total + counts.managers.total + counts.elderlies.total}
                        color="#20293a" on={preset === 'all'}       onClick={() => onApplyPreset('all')}/>
              <StatChip label="유관기관" value={counts.orgs.total}
                        color="#5C82A8" on={preset === 'orgs'}      onClick={() => onApplyPreset('orgs')}/>
              <StatChip label="매니저"   value={counts.managers.total}
                        color={MARKER_HEX.manager} on={preset === 'managers'}  onClick={() => onApplyPreset('managers')}/>
              <StatChip label="어르신"   value={counts.elderlies.total}
                        color={MARKER_HEX.risk.stable} on={preset === 'elderlies'} onClick={() => onApplyPreset('elderlies')}/>
            </div>
          )}
        </div>
        {query && !hasResults && (
          <div style={{
            pointerEvents:'auto', maxWidth:380,
            background:'rgba(255,255,255,0.95)', padding:'8px 12px',
            borderRadius:8, fontSize:11.5, color:'var(--rose-500)', fontWeight:600,
            boxShadow:'var(--shadow-md)',
          }}>
            "{query}" 검색 결과가 없습니다.
          </div>
        )}
      </div>

      {/* 권역 관리 — FAB + 우측 슬라이드 패널. gcare 지역지도의 layer-group FAB를
          이식했다. 지도 우상단은 네이버 줌 컨트롤 자리라 right:64로 비켜 둔다. */}
      {Array.isArray(regions) && regions.length > 0 && !regionPanelOpen && (
        <button type="button"
          onClick={onOpenRegionPanel}
          title="권역 관리"
          aria-label="권역 관리 패널 열기"
          style={{
            position:'absolute', top:20, right:64, zIndex:12, pointerEvents:'auto',
            display:'flex', alignItems:'center', gap:7, padding:'10px 14px',
            borderRadius:12, cursor:'pointer', whiteSpace:'nowrap',
            background:'#fff', border:'1px solid rgba(20,25,40,0.14)',
            boxShadow:'0 2px 10px rgba(20,25,40,0.20)',
            fontSize:12, fontWeight:700, color:'var(--ink-1)',
          }}>
          <span style={{display:'grid', placeItems:'center', fontSize:17, width:17, height:17, color:'var(--orange-600)'}}>{Ic.region}</span>
          권역 관리
        </button>
      )}
      {Array.isArray(regions) && regions.length > 0 && regionPanelOpen && (
        <RegionLayerPanel
          regions={regions}
          selectedId={selectedRegionId ?? null}
          onSelect={onSelectRegion}
          onClose={onCloseRegionPanel}
          selectedSubArea={selectedSubArea}
          onSelectSubArea={onSelectSubArea}
        />
      )}

      {/* 좌하단 범례·필터 / 우측 선택 상세 — 예전 좌·우 컬럼을 지도 위 레이어로.
          스케일바는 네이버 SDK의 scaleControl(우하단)이 이미 그린다. */}
      {legend}
      {detail}
    </NaverMapCanvas>
  );
}

// ─── 상단 통계 칩 ────────────────────────────────────────────────────
// 숫자 + 라벨을 한 알약에 담은 필터 버튼. 활성 칩만 색 테두리를 갖는다.
function StatChip({ label, value, color, on, onClick }) {
  // 지도 타일 위에 얹히는 칩이라 반투명·저채도면 배경 지형과 섞여 읽히지 않는다.
  // 배경은 불투명 흰색으로 고정하고, 활성/비활성은 opacity가 아니라 테두리·글자색
  // 으로만 구분한다(꺼진 칩도 숫자는 또렷하게).
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      title={on ? "전체 보기로 되돌리기" : `${label}만 보기`}
      style={{
        display:'flex', alignItems:'center', gap:6, padding:'7px 11px',
        borderRadius:10, cursor:'pointer', whiteSpace:'nowrap',
        background:'#fff',
        boxShadow: on ? `0 2px 10px ${color}59` : '0 2px 10px rgba(20,25,40,0.20)',
        border: on ? `2px solid ${color}` : '1px solid rgba(20,25,40,0.14)',
        transition:'box-shadow 0.15s, border-color 0.15s',
      }}>
      <span className="mono" style={{fontSize:13.5, fontWeight:800, color, lineHeight:1}}>
        {(value ?? 0).toLocaleString()}
      </span>
      <span style={{fontSize:11, fontWeight:700, color:'var(--ink-1)'}}>{label}</span>
    </button>
  );
}

// ─── 권역 관리 패널 (지도 우측 슬라이드) ──────────────────────────────
// gcare 지역지도의 "권역 관리" 패널 이식. 원본은 권역별 COD 운영기관을 강조했지만
// 여기서는 COD 개념이 케이스 단위(매칭 큐)라, 권역 카드는 운영 지표(등급·담당자·
// 이달 매칭·성공률)와 자원 규모(기관/매니저/어르신)를 보여준다.
function RegionLayerPanel({ regions, selectedId, onSelect, onClose, selectedSubArea, onSelectSubArea }) {
  useEscapeKey(onClose, true);
  const totals = React.useMemo(() => regions.reduce((acc, r) => ({
    orgs: acc.orgs + (r.org_count || 0),
    cases: acc.cases + (r.case_count || 0),
  }), { orgs: 0, cases: 0 }), [regions]);

  return (
    <div
      role="dialog"
      aria-label="권역 관리"
      style={{
        position:'absolute', top:20, right:64, zIndex:13, pointerEvents:'auto',
        width:330, maxHeight:'calc(100% - 40px)', display:'flex', flexDirection:'column',
        background:'rgba(255,255,255,0.97)', backdropFilter:'blur(12px)',
        border:'1px solid rgba(20,25,40,0.08)', borderRadius:14,
        boxShadow:'0 12px 40px rgba(20,25,40,0.18)', overflow:'hidden',
      }}>
      <div style={{display:'flex', alignItems:'center', gap:8, padding:'12px 14px',
                   borderBottom:'1px solid var(--border-soft)'}}>
        <span style={{display:'grid', placeItems:'center', fontSize:16, width:16, height:16, color:'var(--orange-600)'}}>{Ic.region}</span>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight:800}}>권역 관리</div>
          <div className="muted" style={{fontSize:10}}>
            {regions.length}개 권역 · 기관 {totals.orgs.toLocaleString()} · 진행 케이스 {totals.cases.toLocaleString()}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="권역 관리 닫기 (ESC)" title="닫기 (ESC)"
          style={{width:26, height:26, display:'grid', placeItems:'center', borderRadius:7,
                  background:'transparent', border:'1px solid var(--border-soft)',
                  color:'var(--ink-3)', cursor:'pointer'}}>
          {Ic.close}
        </button>
      </div>

      <div style={{overflowY:'auto', padding:10, display:'flex', flexDirection:'column', gap:6}}>
        {/* 전체 보기 — 권역 필터 해제 + 전 권역 bbox로 줌아웃. */}
        <button type="button"
          onClick={() => onSelect(null)}
          aria-pressed={selectedId == null}
          style={{
            display:'flex', alignItems:'center', gap:10, padding:'10px 12px', width:'100%',
            borderRadius:10, textAlign:'left', cursor:'pointer',
            background: selectedId == null ? 'var(--orange-50)' : 'var(--bg-sub)',
            border: selectedId == null ? '1.5px solid var(--orange-500)' : '1px solid var(--border-soft)',
          }}>
          <span style={{width:28, height:28, borderRadius:8, background:'var(--ink-1)',
                        display:'grid', placeItems:'center', color:'#fff', fontSize:14, flex:'0 0 auto'}}>
            <span style={{display:'grid', placeItems:'center', fontSize:15, width:15, height:15}}>{Ic.map}</span>
          </span>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:12.5, fontWeight:800}}>전체 보기</div>
            <div className="muted" style={{fontSize:10}}>권역 필터 해제</div>
          </div>
        </button>

        {regions.map(r => (
          <RegionCard key={r.id} region={r} selected={selectedId === r.id} onSelect={onSelect}
                      selectedSubArea={selectedSubArea} onSelectSubArea={onSelectSubArea}/>
        ))}
      </div>
    </div>
  );
}

function RegionCard({ region, selected, onSelect, selectedSubArea, onSelectSubArea }) {
  const color = regionColorFor(region.code);
  const grade = GRADE_LABEL[region.grade];
  const subAreas = Array.isArray(region.sub_areas) ? region.sub_areas : [];
  // 하위 구역 칩은 카드 안의 별도 버튼이라 카드 전체를 button으로 감쌀 수 없다
  // (버튼 중첩은 잘못된 마크업). 바깥은 div, 권역 본문만 button.
  return (
    <div style={{
      borderRadius:10, background:'var(--surface)',
      border: selected ? `1.5px solid ${color}` : '1px solid var(--border-soft)',
      boxShadow: selected ? `0 0 0 3px ${color}22` : 'none',
      transition:'box-shadow 0.15s, border-color 0.15s', overflow:'hidden',
    }}>
    <button type="button"
      onClick={() => onSelect(region.id)}
      aria-pressed={selected}
      title={`${region.name} 권역으로 이동`}
      style={{
        width:'100%', textAlign:'left', cursor:'pointer', padding:'10px 12px',
        background:'transparent', border:'none',
      }}>
      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
        <span style={{width:26, height:26, borderRadius:8, background:color, flex:'0 0 auto',
                      display:'grid', placeItems:'center', color:'#fff'}}>
          <span style={{display:'grid', placeItems:'center', fontSize:14, width:14, height:14}}>{Ic.region}</span>
        </span>
        <span style={{fontSize:13, fontWeight:800, color}}>{region.name}</span>
        {grade && <span className={"badge badge-" + (GRADE_COLOR[region.grade] || 'sky')} style={{fontSize:9.5}}>{grade}</span>}
        <span className="mono muted" style={{marginLeft:'auto', fontSize:10.5, fontWeight:700}}>
          {(region.org_count || 0).toLocaleString()} 기관
        </span>
      </div>

      <div style={{display:'flex', gap:10, fontSize:10.5, color:'var(--ink-2)', paddingLeft:34}}>
        <span>매니저 <strong className="mono">{region.manager_count || 0}</strong></span>
        <span>어르신 <strong className="mono">{region.elderly_count || 0}</strong></span>
        <span>케이스 <strong className="mono">{region.case_count || 0}</strong></span>
        {region.critical_count > 0 && (
          <span style={{color:'var(--rose-500)', fontWeight:700}}>위험 {region.critical_count}</span>
        )}
      </div>

      {/* 운영 시그널 — 담당자/운영상태 + 이달 매칭·성공률. gcare의 COD 운영기관 줄에 대응. */}
      <div style={{display:'flex', alignItems:'center', gap:6, marginTop:7, padding:'5px 8px',
                   borderRadius:8, background:`${color}0F`, border:`1px solid ${color}26`}}>
        <span style={{fontSize:10.5, fontWeight:700, color:'var(--ink-1)', flex:1, minWidth:0,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
          {region.manager_name || '담당자 미지정'}
          {region.operation_status && OPSTATUS_LABEL[region.operation_status]
            ? ` · ${OPSTATUS_LABEL[region.operation_status]}` : ''}
        </span>
        <span className="mono" style={{fontSize:10, fontWeight:800, color}}>
          매칭 {region.monthly_matches ?? 0} · {Number(region.success_rate ?? 0).toFixed(0)}%
        </span>
      </div>
    </button>

    {/* 하위 구역 — 권역 아래 단계(김해 파일럿의 북부/중부/남부처럼 sub_areas가
        정의된 권역에서만 렌더). 누르면 그 권역으로 이동하면서 어르신을 구역으로
        좁히고, 지도는 해당 구역 폴리곤으로 확대된다. */}
    {subAreas.length > 0 && (
      <div style={{padding:'8px 12px 10px', borderTop:'1px dashed var(--border-soft)',
                   background:`${color}08`}}>
        <div className="muted" style={{fontSize:9.5, fontWeight:800, letterSpacing:'0.03em', marginBottom:6}}>
          하위 구역 {subAreas.length}
        </div>
        <div style={{display:'flex', flexWrap:'wrap', gap:5}}>
          <SubAreaChip label="전체" on={!selectedSubArea} color={color}
                       onClick={() => onSelectSubArea?.(region.id, "")}/>
          {subAreas.map(sa => (
            <SubAreaChip key={sa.name} label={sa.name} count={sa.elderly_count} color={color}
                         on={selectedSubArea === sa.name}
                         onClick={() => onSelectSubArea?.(region.id, sa.name)}/>
          ))}
        </div>
      </div>
    )}
    </div>
  );
}

// 권역 카드 안의 하위 구역 칩. 숫자는 그 구역의 어르신 수.
function SubAreaChip({ label, count, color, on, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      title={on ? "이 구역 필터 해제" : `${label} 구역만 보기`}
      style={{
        display:'inline-flex', alignItems:'center', gap:5, padding:'4px 9px',
        borderRadius:999, fontSize:10.5, fontWeight:700, cursor:'pointer',
        border: `1.2px solid ${on ? color : 'var(--border-soft)'}`,
        background: on ? color : 'var(--surface)',
        color: on ? '#fff' : 'var(--ink-2)',
        transition:'all 0.12s',
      }}>
      {label}
      {count != null && (
        <span className="mono" style={{fontSize:9.5, fontWeight:800, opacity: on ? 0.9 : 0.6}}>{count}</span>
      )}
    </button>
  );
}

// ─── Selection Panel (right) ─────────────────────────────────────────

// 선택된 마커의 상세. 예전엔 340px 우측 컬럼이었지만 이제 지도 위에 떠 있는 카드다 —
// 선택이 없으면 아예 렌더하지 않아 지도를 가리지 않는다("마커를 선택하세요" 빈 컬럼 제거).
// 권역 관리 패널이 열려 있으면 그 왼쪽으로 비켜 선다.
function SelectionPanel({ data, selected, onSelect, onClose, shiftedLeft, topOffset = 76 }) {
  const entity = React.useMemo(() => {
    if (!selected || !data) return null;
    const list = selected.kind === "elderly" ? data.elderlies
              : selected.kind === "manager" ? data.care_managers
              : selected.kind === "org"     ? data.organizations
              : [];
    return list.find(x => x.id === selected.id) || null;
  }, [selected, data]);

  if (!entity) return null;

  return (
    <aside
      aria-label="선택 상세"
      style={{
        position:'absolute', top: topOffset, right: shiftedLeft ? 410 : 64, zIndex:12, pointerEvents:'auto',
        width:330, maxHeight:`calc(100% - ${topOffset + 20}px)`, overflowY:'auto', padding:16,
        background:'rgba(255,255,255,0.97)', backdropFilter:'blur(12px)',
        border:'1px solid rgba(20,25,40,0.08)', borderRadius:14,
        boxShadow:'0 12px 40px rgba(20,25,40,0.18)', transition:'right 0.18s',
      }}>
      <PanelHeader kind={selected.kind} onClose={onClose}/>
      {selected.kind === "elderly" && <ElderlyDetail e={entity} data={data} onSelect={onSelect}/>}
      {selected.kind === "manager" && <ManagerDetail m={entity} data={data} onSelect={onSelect}/>}
      {selected.kind === "org"     && <OrgDetail     o={entity} data={data} onSelect={onSelect}/>}
    </aside>
  );
}

const KIND_BADGE = {
  elderly: { color: "orange", label: "어르신 케이스" },
  manager: { color: "green",  label: "케어매니저" },
  org:     { color: "sky",    label: "유관기관" },
};

function PanelHeader({ kind, onClose }) {
  const kb = KIND_BADGE[kind];
  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14}}>
      <span className={"badge badge-" + kb.color}>{kb.label}</span>
      <button type="button"
        onClick={onClose}
        title="패널 닫기 (ESC)"
        aria-label="패널 닫기"
        style={{width:24, height:24, display:'grid', placeItems:'center', color:'var(--ink-3)', cursor:'pointer', background:'transparent', border:'none'}}>
        {Ic.close}
      </button>
    </div>
  );
}

function ElderlyDetail({ e, data, onSelect }) {
  const rb = { color: RISK_COLOR[e.risk_level] || "green", label: RISK_LABEL[e.risk_level] || e.risk_level };
  const isCritical = e.risk_level === "critical";
  // Haversine 거리 기준 가까운 기관 4건. 좌표가 없는 기관은 자동 탈락.
  const nearestOrgs = React.useMemo(
    () => nearestBy(e.lat, e.lng, data.organizations || [], 4),
    [e.lat, e.lng, data.organizations]
  );
  // case_id가 있으면 cod 화면이 정확히 이 어르신의 매칭 큐로 직진하도록 전달.
  const codHref = e.active_case_id ? `/cod?case_id=${e.active_case_id}` : `/cod`;
  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:6}}>
        <AvatarImg className="avatar lg" url={e.avatar_url} name={e.name}/>
        <div style={{minWidth:0}}>
          <div style={{fontSize:16, fontWeight:700}}>
            {e.name} 어르신
            {e.age != null && <span className="muted" style={{fontSize:12, fontWeight:500, marginLeft:6}}>{e.age}세</span>}
          </div>
          <div className="muted" style={{fontSize:11}}>{e.health_status || '건강상태 정보 없음'}</div>
        </div>
      </div>
      <span className={"badge badge-" + rb.color} style={{marginBottom:14, display:'inline-block'}}>{rb.label} 케이스</span>
      {e.address && (
        <div className="muted" style={{fontSize:11.5, marginBottom:14, lineHeight:1.55}}>
          📍 {e.address}
        </div>
      )}

      <div className="h3" style={{marginBottom:8, fontSize:13, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <span>가까운 자원</span>
        <span className="muted" style={{fontSize:10, fontWeight:500}}>직선 거리</span>
      </div>
      {nearestOrgs.length === 0 && (
        <div className="muted" style={{padding:'10px 0', fontSize:11.5}}>거리 정보가 있는 기관이 없습니다.</div>
      )}
      {nearestOrgs.map((o, i) => (
        <button key={o.id} type="button"
          onClick={() => onSelect?.({ kind: "org", id: o.id })}
          title={`${o.name} 상세 보기`}
          style={{
            display:'flex', alignItems:'center', gap:10, padding:'10px 0',
            borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
            width:'100%', textAlign:'left', background:'transparent', border:'none', cursor:'pointer',
          }}>
          <span style={{
            width:30, height:30, borderRadius:8,
            background:`var(--${CAT_COLOR[o.category]}-50)`,
            color:`var(--${CAT_COLOR[o.category]}-${CAT_COLOR[o.category] === 'amber' ? '500' : '600'})`,
            display:'grid', placeItems:'center', fontSize:9, fontWeight:800,
          }}>{CAT_LABEL[o.category]}</span>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:12.5, fontWeight:600}}>{o.name}</div>
            <div className="muted" style={{fontSize:10}}>{o.address}</div>
          </div>
          <span className="mono" style={{fontSize:11, fontWeight:700, color:'var(--orange-600)'}}>{formatKm(o._km)}</span>
        </button>
      ))}

      <a href={codHref} className="btn btn-primary btn-md"
         style={{width:'100%', marginTop:16, textDecoration:'none', display:'inline-flex', justifyContent:'center'}}>
        매니저 매칭 (AI) →
      </a>
      <a href="/dashboard/region" className="btn btn-ghost btn-md"
         style={{width:'100%', marginTop:6, textDecoration:'none', display:'inline-flex', justifyContent:'center'}}>
        권역 대시보드
      </a>
      {/* 119 호출 — 위험 등급에선 강한 rose 톤으로 강조, 그 외는 보조 톤 */}
      <a href="tel:119"
         className="btn btn-md"
         style={{
           width:'100%', marginTop:6, textDecoration:'none', display:'inline-flex', justifyContent:'center',
           background: isCritical ? 'var(--rose-500)' : 'var(--rose-50)',
           color:      isCritical ? '#fff'             : 'var(--rose-500)',
           border:     isCritical ? 'none'             : '1px solid var(--rose-200)',
           fontWeight: 700,
         }}>
        🚨 119 긴급 호출
      </a>
    </>
  );
}

function ManagerDetail({ m, data, onSelect }) {
  const sb = { color: STATUS_COLOR[m.status] || "gray", label: STATUS_LABEL[m.status] || m.status };
  // 이 매니저 위치 기준으로 가까운 어르신 4명 — 동선 결정에 직관적인 정보.
  const nearestElderlies = React.useMemo(
    () => nearestBy(m.lat, m.lng, data?.elderlies || [], 4),
    [m.lat, m.lng, data?.elderlies]
  );
  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
        <AvatarImg className="avatar lg" style={{background:'linear-gradient(135deg, var(--orange-300), var(--orange-500))', color:'#fff'}} url={m.avatar_url} name={m.name}/>
        <div style={{minWidth:0}}>
          <div style={{fontSize:16, fontWeight:700}}>{m.name} 매니저</div>
          <div className="muted" style={{fontSize:11}}>{m.qualification || '자격 정보 없음'}</div>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14}}>
        <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10}}>
          <div className="muted" style={{fontSize:10, fontWeight:600}}>상태</div>
          <div style={{marginTop:4}}>
            <span className={"badge badge-" + sb.color}>{sb.label}</span>
          </div>
        </div>
        <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10}}>
          <div className="muted" style={{fontSize:10, fontWeight:600}}>담당 케이스</div>
          <div className="mono" style={{fontSize:18, fontWeight:800, marginTop:2}}>
            {m.assigned_cases ?? '-'}
          </div>
        </div>
        <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10, gridColumn:'1 / -1'}}>
          <div className="muted" style={{fontSize:10, fontWeight:600}}>소속</div>
          <div style={{fontSize:12.5, fontWeight:600, marginTop:2}}>{m.organization_name || '소속 미배정'}</div>
        </div>
        {m.rating != null && (
          <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10, gridColumn:'1 / -1'}}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>평가 점수</div>
            <div className="mono" style={{fontSize:14, fontWeight:800, marginTop:2}}>
              ★ {Number(m.rating).toFixed(1)} / 5
            </div>
          </div>
        )}
      </div>

      {nearestElderlies.length > 0 && (
        <>
          <div className="h3" style={{marginBottom:8, fontSize:13, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span>가까운 어르신</span>
            <span className="muted" style={{fontSize:10, fontWeight:500}}>직선 거리</span>
          </div>
          {nearestElderlies.map((e, i) => {
            const rc = RISK_COLOR[e.risk_level] || "green";
            return (
              <button key={e.id} type="button"
                onClick={() => onSelect?.({ kind: "elderly", id: e.id })}
                title={`${e.name} 어르신 상세`}
                style={{
                  display:'flex', alignItems:'center', gap:10, padding:'10px 0',
                  borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
                  width:'100%', textAlign:'left', background:'transparent', border:'none', cursor:'pointer',
                }}>
                <span style={{
                  width:30, height:30, borderRadius:8,
                  background:`var(--${rc}-50)`, color:`var(--${rc}-600)`,
                  display:'grid', placeItems:'center', fontSize:10, fontWeight:800,
                }}>{RISK_LABEL[e.risk_level] || ''}</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12.5, fontWeight:600}}>{e.name} 어르신</div>
                  <div className="muted" style={{fontSize:10, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {e.address || e.health_status || ''}
                  </div>
                </div>
                <span className="mono" style={{fontSize:11, fontWeight:700, color:'var(--orange-600)'}}>{formatKm(e._km)}</span>
              </button>
            );
          })}
        </>
      )}

      <a href={`/managers?id=${m.id}`} className="btn btn-primary btn-md"
         style={{width:'100%', marginTop:16, textDecoration:'none', display:'inline-flex', justifyContent:'center'}}>
        매니저 프로필 →
      </a>
    </>
  );
}

function OrgDetail({ o, data, onSelect }) {
  const cc = CAT_COLOR[o.category];
  const services = (o.services || "").split(",").map(s => s.trim()).filter(Boolean);
  const cb = CONTRACT_LABEL[o.contract_status]
           ? { color: CONTRACT_COLOR[o.contract_status], label: CONTRACT_LABEL[o.contract_status] }
           : null;
  // 기관 위치 기준 가까운 매니저 4명 — 협력 동선 결정용.
  const nearestManagers = React.useMemo(
    () => nearestBy(o.lat, o.lng, data?.care_managers || [], 4),
    [o.lat, o.lng, data?.care_managers]
  );
  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
        <div style={{
          width:48, height:48, borderRadius:12,
          background: MARKER_HEX.category[o.category] || `var(--${cc}-500)`,
          display:'grid', placeItems:'center',
          boxShadow:'0 3px 8px rgba(0,0,0,0.16)',
        }}>
          <MarkerGlyph glyph={CAT_GLYPH[o.category] || 'welfare'} size={24}
                       bg={MARKER_HEX.category[o.category] || '#5C82A8'}/>
        </div>
        <div style={{minWidth:0}}>
          <div style={{fontSize:16, fontWeight:700}}>{o.name}</div>
          <div style={{display:'flex', gap:6, marginTop:4, flexWrap:'wrap'}}>
            <span className={"badge badge-" + cc}>{CAT_LABEL[o.category]}</span>
            {cb && <span className={"badge badge-" + cb.color}>{cb.label}</span>}
          </div>
        </div>
      </div>

      {o.address && (
        <div style={{fontSize:12, color:'var(--ink-2)', marginBottom:8, lineHeight:1.55}}>
          📍 {o.address}
        </div>
      )}
      {o.contact_name && (
        <div className="muted" style={{fontSize:11.5, marginBottom:14}}>
          담당: {o.contact_name}
        </div>
      )}

      {services.length > 0 && (
        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>제공 서비스</div>
          <div style={{display:'flex', gap:5, flexWrap:'wrap'}}>
            {services.map((s, i) => (
              <span key={i} style={{
                padding:'3px 9px', fontSize:10.5, fontWeight:600,
                background:'var(--bg-sub)', borderRadius:99, color:'var(--ink-2)',
              }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {nearestManagers.length > 0 && (
        <>
          <div className="h3" style={{marginBottom:8, fontSize:13, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span>가까운 매니저</span>
            <span className="muted" style={{fontSize:10, fontWeight:500}}>직선 거리</span>
          </div>
          {nearestManagers.map((m, i) => {
            const dc = STATUS_COLOR[m.status] || "gray";
            return (
              <button key={m.id} type="button"
                onClick={() => onSelect?.({ kind: "manager", id: m.id })}
                title={`${m.name} 매니저 상세`}
                style={{
                  display:'flex', alignItems:'center', gap:10, padding:'10px 0',
                  borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none',
                  width:'100%', textAlign:'left', background:'transparent', border:'none', cursor:'pointer',
                }}>
                <div className="avatar sm" style={{background:'linear-gradient(135deg, var(--orange-300), var(--orange-500))', color:'#fff'}}>
                  {m.name[0]}
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12.5, fontWeight:600}}>{m.name}</div>
                  <div className="muted" style={{fontSize:10}}>
                    {m.qualification || ''} · <span style={{color:`var(--${dc}-600)`}}>{STATUS_LABEL[m.status] || m.status}</span>
                  </div>
                </div>
                <span className="mono" style={{fontSize:11, fontWeight:700, color:'var(--orange-600)'}}>{formatKm(m._km)}</span>
              </button>
            );
          })}
        </>
      )}

      <a href={`/organizations?id=${o.id}`} className="btn btn-primary btn-md"
         style={{width:'100%', marginTop:16, textDecoration:'none', display:'inline-flex', justifyContent:'center', marginBottom:6}}>
        기관 상세 →
      </a>
      {o.phone ? (
        <a href={`tel:${o.phone}`} className="btn btn-ghost btn-md"
           style={{width:'100%', textDecoration:'none', display:'inline-flex', justifyContent:'center'}}>
          📞 {o.phone}
        </a>
      ) : (
        <button type="button" disabled title="전화번호 미등록" className="btn btn-ghost btn-md"
                style={{width:'100%'}}>
          전화 없음
        </button>
      )}
    </>
  );
}

export { MapView };
